import { Box, InputBase, Tooltip } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';

interface InlineEditProps {
    value: string;
    onSubmit: (value: string) => void;
    onCancel: () => void;
    validate?: (value: string) => string | null;
    fontSize?: number;
    fontWeight?: number;
    tooltipOffset?: [number, number];
}

export default function InlineEdit({
    value: initialValue,
    onSubmit,
    onCancel,
    validate,
    fontSize = 14,
    fontWeight = 500,
    tooltipOffset = [-4, -8],
}: InlineEditProps) {
    const [value, setValue] = useState(initialValue);
    const inputRef = useRef<HTMLInputElement>(null);

    const error = useMemo(() => validate?.(value) ?? null, [value, validate]);
    const tooltipContent = error || 'Enter to accept, Esc to discard';

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, []);

    const handleSubmit = () => {
        const trimmedValue = value.trim();
        if (!error && trimmedValue !== initialValue) {
            onSubmit(trimmedValue);
        }
        onCancel();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        // Only swallow keys this component actually handles. Blanket-stopping
        // every keydown breaks global shortcuts (e.g. Ctrl+H Safe Mode toggle)
        // when the user has focused this inline editor.
        if (e.key === 'Enter') {
            e.stopPropagation();
            handleSubmit();
        } else if (e.key === 'Escape') {
            e.stopPropagation();
            onCancel();
        }
    };

    return (
        <Tooltip
            open
            placement="bottom-start"
            title={tooltipContent}
            PopperProps={{
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: tooltipOffset,
                        },
                    },
                ],
            }}
            componentsProps={{
                tooltip: {
                    sx: error
                        ? {
                              bgcolor: 'error.main',
                              '& .MuiTooltip-arrow': {
                                  color: 'error.main',
                              },
                          }
                        : {},
                },
            }}
        >
            <Box>
                <InputBase
                    inputRef={inputRef}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onBlur={handleSubmit}
                    onKeyDown={handleKeyDown}
                    sx={{
                        fontSize,
                        fontWeight,
                        padding: 0,
                        '& input': {
                            padding: 0,
                            color: error ? 'error.main' : 'inherit',
                        },
                    }}
                    // The test id is forwarded to the inner <input> rather
                    // than the wrapper <div>, so Playwright's toBeFocused()
                    // and inputValue() target the actual focused element.
                    inputProps={{ 'data-testid': 'inline-edit-input' }}
                />
            </Box>
        </Tooltip>
    );
}
